// v2.19.34 — Wizard modal pour revendiquer ("Maison Koffi") une adresse en 3 étapes :
// 1) GPS lock → calcule address_civ depuis lat/lng via /from-geoloc
// 2) Photo devanture (porte/portail/façade — PAS de selfie pour vie privée)
// 3) Nom + statut (propriétaire/locataire/occupant) + visibilité publique
//
// Soumission : POST /api/v3/addresses/claim
import { useState, useRef } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "civ_user_token";

function _authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ClaimAddressWizard({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [gps, setGps] = useState(null); // { lat, lng, preview }
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null); // 'denied' | 'unavailable' | 'timeout'
  const [liveAcc, setLiveAcc] = useState(null); // précision en direct pendant l'échantillonnage
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [name, setName] = useState("");
  const [claimType, setClaimType] = useState("proprietaire");
  const [isPublic, setIsPublic] = useState(true);
  const [sharePhoto, setSharePhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setStep(1); setGps(null); setPhotoBase64(null); setPhotoPreview(null);
    setName(""); setClaimType("proprietaire"); setIsPublic(true); setSharePhoto(false);
  };
  const close = () => { reset(); onClose(); };

  // ── Step 1 : GPS lock ──
  // Cas mobile fréquent : la préview intégrée (iframe) bloque la géoloc → PERMISSION_DENIED
  // immédiat. On détecte et on guide l'utilisateur au lieu d'un simple toast.
  const isEmbedded = (() => { try { return window.self !== window.top; } catch { return true; } })();

  const resolveAddress = async (pos) => {
    const lat = pos.coords.latitude, lng = pos.coords.longitude;
    const accuracy = Math.round(pos.coords.accuracy || 0);
    try {
      const token = localStorage.getItem("civ_user_token") || "";
      const r = await fetch(`${API}/v3/addresses/from-geoloc`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ lat, lng }),
      });
      const d = await r.json();
      if (d.ok) {
        setGps({ lat, lng, accuracy, preview: d.preview, nearby: (d.nearby_claimed || [])[0] || null });
      } else {
        toast.error(d.reason || "Impossible de calculer l'adresse à cette position");
      }
    } catch (_) { toast.error("Erreur réseau"); }
    setGpsLoading(false);
  };

  const lockGps = () => {
    if (!navigator.geolocation) { setGpsError("unavailable"); return; }
    setGpsLoading(true);
    setGpsError(null);
    setLiveAcc(null);
    // v5.3 — Stabilisation : on échantillonne le GPS ~8 s et on garde la mesure la
    // plus précise (une mesure unique fluctue de ±5-15 m → codes voisins instables).
    let best = null, done = false, wid = null, tid = null;
    const finish = () => {
      if (done) return;
      done = true;
      if (wid != null) navigator.geolocation.clearWatch(wid);
      if (tid) clearTimeout(tid);
      if (!best) { setGpsLoading(false); setGpsError("timeout"); return; }
      resolveAddress(best);
    };
    wid = navigator.geolocation.watchPosition((pos) => {
      if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
      setLiveAcc(Math.round(pos.coords.accuracy));
      if (pos.coords.accuracy <= 8) finish(); // signal excellent : inutile d'attendre
    }, (err) => {
      if (best) return; // on a déjà une mesure exploitable
      done = true;
      if (wid != null) navigator.geolocation.clearWatch(wid);
      if (tid) clearTimeout(tid);
      setGpsLoading(false);
      setGpsError(err.code === 1 ? "denied" : err.code === 3 ? "timeout" : "unavailable");
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
    tid = setTimeout(finish, 8000);
  };

  // ── Step 2 : Photo ──
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Photo trop lourde (max 3 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoBase64(reader.result);
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(f);
  };

  // ── Step 3 : Submit ──
  const submit = async () => {
    if (!gps || !photoBase64 || !name.trim()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/v3/addresses/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ..._authHeaders() },
        body: JSON.stringify({
          user_lat: gps.lat, user_lng: gps.lng,
          address_civ: gps.preview.address_civ,
          friendly_name: name.trim(),
          claim_type: claimType,
          is_public: isPublic,
          share_photo: sharePhoto,
          claim_photo_base64: photoBase64,
        }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        toast.success(`✅ ${d.friendly_name} revendiquée !`);
        onSuccess?.(d);
        close();
      } else if (d.error === 'already_claimed') {
        toast.error(d.claimed_by_self ? "Tu as déjà revendiqué cette adresse." : `Adresse déjà revendiquée sous "${d.friendly_name}"`);
      } else if (d.error === 'too_far_from_address') {
        toast.error(`Trop loin de l'adresse (${d.distance_m}m / max ${d.max_m}m)`);
      } else if (d.error === 'name_blocked') {
        toast.error(`Nom interdit : "${d.banned}"`);
      } else {
        toast.error(d.error || `Erreur ${r.status}`);
      }
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setSubmitting(false); }
  };

  return (
    <div onClick={close} data-testid="claim-wizard-overlay" style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} data-testid="claim-wizard" style={modalStyle}>
        <header style={headerStyle}>
          <strong>🏠 Revendiquer mon adresse</strong>
          <button onClick={close} data-testid="claim-wizard-close" style={closeBtnStyle}>×</button>
        </header>

        <div style={stepsBarStyle}>
          {[1, 2, 3].map((n) => (
            <div key={n} style={{ ...stepDotStyle, background: step >= n ? "#FBBF24" : "rgba(255,255,255,0.15)", color: step >= n ? "#0F172A" : "#94A3B8" }}>{n}</div>
          ))}
        </div>

        {step === 1 && (
          <div data-testid="claim-step-1">
            <h3 style={h3Style}>📍 1. Localise-toi</h3>
            <p style={pStyle}>On a besoin de ta position GPS pour calculer ton code adresse et vérifier que tu es bien sur place.</p>
            {!gps && (
              <button onClick={lockGps} disabled={gpsLoading} data-testid="claim-gps-btn" style={primaryBtnStyle}>
                {gpsLoading ? `📡 Stabilisation du GPS…${liveAcc != null ? ` ±${liveAcc} m` : ""}` : "📍 Activer ma position"}
              </button>
            )}
            {!gps && gpsError && (
              <div data-testid="claim-gps-error" style={{ marginTop: 12, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 12, padding: "12px 14px", fontSize: 13, lineHeight: 1.5, color: "#FECACA" }}>
                {gpsError === "denied" && isEmbedded && (
                  <>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>🔒 La préview intégrée bloque le GPS</div>
                    <div>Tu utilises l'aperçu Emergent : il empêche l'accès à la position. Ouvre l'app directement dans ton navigateur puis réessaie :</div>
                    <button
                      onClick={() => window.open(window.location.href, "_blank", "noopener")}
                      data-testid="claim-gps-open-browser"
                      style={{ ...primaryBtnStyle, marginTop: 10, background: "#3B82F6", color: "#fff" }}
                    >
                      🌐 Ouvrir en plein écran
                    </button>
                  </>
                )}
                {gpsError === "denied" && !isEmbedded && (
                  <>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>🚫 Permission refusée pour la géoloc</div>
                    <div>Pour réactiver : appuie sur le <strong>cadenas 🔒</strong> (ou ⓘ) à côté de l'adresse du site → <strong>Autorisations</strong> → <strong>Localisation</strong> → <strong>Autoriser</strong>. Vérifie aussi que le GPS du téléphone est allumé, puis réessaie.</div>
                    <button onClick={lockGps} data-testid="claim-gps-retry" style={{ ...primaryBtnStyle, marginTop: 10 }}>🔄 Réessayer</button>
                  </>
                )}
                {gpsError === "timeout" && (
                  <>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>⏱️ Position introuvable à temps</div>
                    <div>Active le GPS, rapproche-toi d'une fenêtre ou sors à l'extérieur, puis réessaie.</div>
                    <button onClick={lockGps} data-testid="claim-gps-retry" style={{ ...primaryBtnStyle, marginTop: 10 }}>🔄 Réessayer</button>
                  </>
                )}
                {gpsError === "unavailable" && (
                  <>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>📡 GPS indisponible</div>
                    <div>Vérifie que la localisation du téléphone est activée (réglages rapides), puis réessaie.</div>
                    <button onClick={lockGps} data-testid="claim-gps-retry" style={{ ...primaryBtnStyle, marginTop: 10 }}>🔄 Réessayer</button>
                  </>
                )}
              </div>
            )}
            {gps && (
              <div data-testid="claim-gps-result" style={resultBoxStyle}>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>Ton adresse calculée :</div>
                <div style={{ fontFamily: "monospace", color: "#93C5FD", fontSize: 14, fontWeight: 700, margin: "4px 0" }}>{gps.preview.address_civ}</div>
                <div style={{ fontSize: 12, color: "#86EFAC" }}>📍 {gps.preview.commune_name}, {gps.preview.district_name}</div>
                {gps.accuracy != null && gps.accuracy > 0 && (
                  <div data-testid="claim-gps-accuracy" style={{ fontSize: 12, marginTop: 6, color: gps.accuracy <= 25 ? "#86EFAC" : "#FCD34D" }}>
                    📡 Précision GPS : ±{gps.accuracy} m {gps.accuracy <= 25 ? "✅" : "⚠️"}
                    {gps.accuracy > 25 && <div style={{ color: "#FCD34D", marginTop: 2 }}>Position approximative — sors à l'extérieur ou utilise un téléphone pour plus de fiabilité.</div>}
                  </div>
                )}

                {gps.nearby ? (
                  <div data-testid="claim-nearby" style={{ marginTop: 12, background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.45)", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, color: "#BFDBFE" }}>
                    {gps.nearby.claimed_by_me ? (
                      <>
                        <div style={{ fontWeight: 800, marginBottom: 4 }}>✅ C'est déjà ton adresse !</div>
                        <div>« <strong>{gps.nearby.friendly_name}</strong> » (<code style={{ color: "#93C5FD" }}>{gps.nearby.address_civ}</code>) est revendiquée à <strong>{gps.nearby.distance_m} m</strong> d'ici. Inutile d'en créer une nouvelle.</div>
                        <button onClick={close} data-testid="claim-nearby-ok" style={{ ...primaryBtnStyle, marginTop: 10 }}>OK, garder mon adresse</button>
                        <button onClick={() => setStep(2)} data-testid="claim-nearby-anyway" style={{ ...linkBtnStyle, marginTop: 4 }}>C'est une autre adresse ici (cour commune…) → continuer</button>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 800, marginBottom: 4 }}>ℹ️ Une adresse existe déjà ici</div>
                        <div>« <strong>{gps.nearby.friendly_name || "Adresse privée"}</strong> » (<code style={{ color: "#93C5FD" }}>{gps.nearby.address_civ}</code>) est déjà revendiquée à <strong>{gps.nearby.distance_m} m</strong>. Si c'est le même endroit, ne crée pas de doublon.</div>
                        <button onClick={() => setStep(2)} data-testid="claim-nearby-anyway" style={{ ...primaryBtnStyle, marginTop: 10 }}>C'est une adresse différente → continuer</button>
                      </>
                    )}
                  </div>
                ) : (
                  <button onClick={() => setStep(2)} data-testid="claim-next-1" style={{ ...primaryBtnStyle, marginTop: 14 }}>Continuer →</button>
                )}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid="claim-step-2">
            <h3 style={h3Style}>📸 2. Photo de ta devanture</h3>
            <p style={pStyle}>Prends une photo de ta <strong>porte, portail ou façade</strong> (PAS de selfie). Ce sera ton repère visuel pour les livreurs.</p>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={onFile} data-testid="claim-photo-input" style={{ display: "none" }} />
            {!photoPreview && (
              <button onClick={() => fileInputRef.current?.click()} data-testid="claim-photo-btn" style={primaryBtnStyle}>📷 Prendre la photo</button>
            )}
            {photoPreview && (
              <div>
                <img src={photoPreview} alt="devanture" data-testid="claim-photo-preview" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 10, marginBottom: 12 }} />
                <button onClick={() => fileInputRef.current?.click()} data-testid="claim-photo-retake" style={{ ...secondaryBtnStyle, marginRight: 8 }}>↻ Reprendre</button>
                <button onClick={() => setStep(3)} data-testid="claim-next-2" style={primaryBtnStyle}>Continuer →</button>
              </div>
            )}
            <button onClick={() => setStep(1)} data-testid="claim-back-2" style={linkBtnStyle}>← Retour</button>
          </div>
        )}

        {step === 3 && (
          <div data-testid="claim-step-3">
            <h3 style={h3Style}>🏷 3. Nomme ton adresse</h3>
            <p style={pStyle}>Donne-lui un nom mémorable que tu pourras partager (ex: "Maison Koffi", "Boutique Aya").</p>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value.slice(0, 40))}
              maxLength={40} placeholder="Ex: Maison Koffi" data-testid="claim-name-input"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: "#64748b", marginTop: -8, marginBottom: 14 }}>{name.length}/40 caractères</div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Tu es :</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { v: "proprietaire", l: "🏠 Propriétaire" },
                  { v: "locataire", l: "🔑 Locataire" },
                  { v: "occupant", l: "👤 Occupant" },
                ].map((o) => (
                  <button key={o.v} onClick={() => setClaimType(o.v)} data-testid={`claim-type-${o.v}`}
                    style={{ ...chipStyle, background: claimType === o.v ? "#FBBF24" : "rgba(255,255,255,0.06)", color: claimType === o.v ? "#0F172A" : "#E8ECF7" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <label style={checkLabelStyle}>
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="claim-public-checkbox" />
              <span>Visible dans la recherche publique</span>
            </label>
            <label style={checkLabelStyle}>
              <input type="checkbox" checked={sharePhoto} onChange={(e) => setSharePhoto(e.target.checked)} disabled={!isPublic} data-testid="claim-share-photo-checkbox" />
              <span style={{ opacity: isPublic ? 1 : 0.5 }}>Inclure la photo dans les previews de partage (WhatsApp, Twitter…)</span>
            </label>

            <button onClick={submit} disabled={!name.trim() || submitting} data-testid="claim-submit" style={{ ...primaryBtnStyle, marginTop: 14, opacity: !name.trim() || submitting ? 0.5 : 1 }}>
              {submitting ? "Envoi…" : "✅ Revendiquer mon adresse"}
            </button>
            <button onClick={() => setStep(2)} data-testid="claim-back-3" style={linkBtnStyle}>← Retour</button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle = { position: "fixed", inset: 0, background: "rgba(4,18,11,0.72)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 14 };
const modalStyle = { background: "radial-gradient(110% 80% at 85% 20%, rgba(14,90,50,0.5) 0%, transparent 55%), linear-gradient(160deg, #0A3A22 0%, #062818 50%, #051F13 100%)", color: "#ECF5EE", borderRadius: 22, padding: 22, maxWidth: 460, width: "100%", maxHeight: "90vh", overflowY: "auto", border: "1px solid rgba(201,162,75,0.35)", boxShadow: "0 24px 70px rgba(0,0,0,0.6)", fontFamily: "'Manrope','Inter',sans-serif" };
const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, fontSize: 16, color: "#F2C66B", fontWeight: 800 };
const closeBtnStyle = { background: "none", border: "none", color: "#94A3B8", fontSize: 28, cursor: "pointer", lineHeight: 1, padding: 0 };
const stepsBarStyle = { display: "flex", justifyContent: "space-between", gap: 6, marginBottom: 20 };
const stepDotStyle = { flex: 1, height: 6, borderRadius: 999, textAlign: "center", fontSize: 0 };
const h3Style = { fontSize: 16, margin: "0 0 8px", color: "#fff" };
const pStyle = { fontSize: 13, color: "#94A3B8", margin: "0 0 16px", lineHeight: 1.5 };
const primaryBtnStyle = { background: "linear-gradient(135deg, #16A34A, #22C55E)", color: "#0F172A", border: "none", padding: "12px 22px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", boxShadow: "0 4px 14px rgba(34,197,94,0.35)", width: "100%" };
const secondaryBtnStyle = { background: "rgba(255,255,255,0.06)", color: "#E8ECF7", border: "1px solid rgba(255,255,255,0.12)", padding: "10px 18px", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" };
const linkBtnStyle = { background: "none", border: "none", color: "#94A3B8", marginTop: 14, fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: 0 };
const resultBoxStyle = { background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 12, padding: 16, marginTop: 6 };
const inputStyle = { width: "100%", padding: "12px 14px", borderRadius: 10, background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", fontSize: 15, fontFamily: "inherit", outline: "none", marginBottom: 4, boxSizing: "border-box" };
const chipStyle = { padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit" };
const checkLabelStyle = { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#E8ECF7", marginBottom: 10, cursor: "pointer", lineHeight: 1.4 };
